(function (globalScope) {
  'use strict';

  if (globalScope.document && globalScope.BeetlesEmailAutocomplete) {
    globalScope.BeetlesEmailAutocomplete.initEmailAutocomplete();
    return;
  }

  const EMAIL_DOMAINS = Object.freeze([
    'gmail.com', 'outlook.com', 'hotmail.com', 'yahoo.com', 'icloud.com', 'aol.com',
    'live.com', 'msn.com', 'comcast.net', 'att.net', 'verizon.net', 'sbcglobal.net',
    'me.com', 'mac.com', 'proton.me', 'protonmail.com', 'mail.com', 'gmx.com', 'gmx.de', 'web.de',
    'yahoo.co.uk', 'yahoo.co.jp', 'hotmail.co.uk', 'btinternet.com', 'orange.fr', 'free.fr',
    'qq.com', '163.com', '126.com', 'foxmail.com', 'yeah.net', 'sina.com', 'sohu.com', '139.com',
    '189.cn', 'aliyun.com',
  ]);
  const MAX_SUGGESTIONS = 12;
  const INPUT_SELECTOR = 'input[type="email"]';
  const instances = new WeakMap();

  function getEmailSuggestions(value, domains = EMAIL_DOMAINS, limit = MAX_SUGGESTIONS) {
    const email = String(value || '').trim();
    if (!email || /\s/.test(email)) return [];
    const parts = email.split('@');
    if (parts.length > 2 || !parts[0]) return [];
    const prefix = (parts[1] || '').toLowerCase();
    return domains.filter(domain => domain.startsWith(prefix) && domain !== prefix)
      .slice(0, limit).map(domain => parts[0] + '@' + domain);
  }

  function createInstance(doc) {
    const win = doc.defaultView;
    const list = doc.createElement('ul');
    let suffix = 1;
    do { list.id = 'BeetlesEmailSuggestions-' + suffix++; } while (doc.getElementById(list.id));
    list.className = 'email-autocomplete__list';
    list.setAttribute('role', 'listbox');
    list.setAttribute('aria-label', 'Email suggestions');
    list.hidden = true;
    doc.body.appendChild(list);
    let activeInput = null;
    let activeIndex = -1;
    let selecting = false;
    let frame = 0;
    const boundInputs = new WeakSet();

    function bind(input) {
      if (!input?.matches?.(INPUT_SELECTOR) || boundInputs.has(input)) return;
      boundInputs.add(input);
      input.setAttribute('role', 'combobox');
      input.setAttribute('aria-autocomplete', 'list');
      input.setAttribute('aria-controls', list.id);
      input.setAttribute('aria-expanded', 'false');
      input.setAttribute('autocomplete', 'off');
      input.removeAttribute('list');
    }

    function scan(root) {
      bind(root);
      root.querySelectorAll?.(INPUT_SELECTOR).forEach(bind);
    }

    function close(root) {
      if (root && activeInput && root !== activeInput && !root.contains(activeInput)) return;
      activeInput?.setAttribute('aria-expanded', 'false');
      activeInput?.removeAttribute('aria-activedescendant');
      activeInput = null;
      activeIndex = -1;
      list.hidden = true;
      list.replaceChildren();
      if (list.parentElement !== doc.body) doc.body.appendChild(list);
    }

    function available(input) {
      return input?.isConnected && input.matches(INPUT_SELECTOR) && !input.matches(':disabled') && !input.readOnly
        && !input.closest('[hidden], [inert], [aria-hidden="true"], dialog:not([open])')
        && input.getClientRects().length > 0 && win.getComputedStyle(input).visibility !== 'hidden';
    }

    function positionList() {
      frame = 0;
      if (!activeInput || list.hidden) return;
      if (!available(activeInput)) { close(); return; }
      const host = activeInput.closest('dialog[open]') || doc.body;
      if (list.parentElement !== host) host.appendChild(list);
      const rect = activeInput.getBoundingClientRect();
      const viewport = win.visualViewport;
      const leftEdge = viewport?.offsetLeft || 0;
      const topEdge = viewport?.offsetTop || 0;
      const width = viewport?.width || win.innerWidth;
      const height = viewport?.height || win.innerHeight;
      if (rect.bottom <= topEdge || rect.top >= topEdge + height) { close(); return; }
      const below = Math.max(0, topEdge + height - rect.bottom - 14);
      const above = Math.max(0, rect.top - topEdge - 14);
      const maxHeight = Math.min(width <= 749 ? 164 : 196, Math.max(below, above));
      const openAbove = below < Math.min(maxHeight, list.scrollHeight) && above > below;
      list.style.width = Math.min(rect.width, Math.max(0, width - 16)) + 'px';
      list.style.left = Math.max(leftEdge + 8, Math.min(rect.left, leftEdge + width - rect.width - 8)) + 'px';
      list.style.maxHeight = Math.min(maxHeight, openAbove ? above : below) + 'px';
      list.style.top = (openAbove ? rect.top - list.getBoundingClientRect().height - 6 : rect.bottom + 6) + 'px';
    }

    function schedulePosition() {
      if (activeInput && !frame) frame = win.requestAnimationFrame(positionList);
    }

    function setActive(index) {
      const count = list.children.length;
      if (!activeInput || !count) return;
      activeIndex = (index + count) % count;
      Array.from(list.children).forEach((option, i) => option.setAttribute('aria-selected', String(i === activeIndex)));
      const option = list.children[activeIndex];
      activeInput.setAttribute('aria-activedescendant', option.id);
      option.scrollIntoView?.({ block: 'nearest' });
    }

    function render(input) {
      if (selecting || doc.activeElement !== input) return;
      close();
      if (!available(input)) return;
      bind(input);
      const matches = getEmailSuggestions(input.value);
      if (!matches.length) return;
      activeInput = input;
      matches.forEach((email, index) => {
        const option = doc.createElement('li');
        option.id = list.id + '-' + index;
        option.dataset.emailAutocompleteOption = email;
        option.tabIndex = -1;
        option.setAttribute('role', 'option');
        option.setAttribute('aria-selected', 'false');
        const at = email.indexOf('@');
        const name = doc.createElement('span');
        name.textContent = email.slice(0, at);
        const domain = doc.createElement('span');
        domain.className = 'email-autocomplete__domain';
        domain.textContent = email.slice(at);
        option.append(name, domain);
        list.appendChild(option);
      });
      list.hidden = false;
      input.setAttribute('aria-expanded', 'true');
      positionList();
    }

    function choose(option) {
      if (!activeInput || !option || !available(activeInput)) { close(); return; }
      const input = activeInput;
      input.value = option.dataset.emailAutocompleteOption;
      input.setCustomValidity('');
      selecting = true;
      close();
      try {
        input.focus({ preventScroll: true });
        input.dispatchEvent(new win.Event('input', { bubbles: true }));
        input.dispatchEvent(new win.Event('change', { bubbles: true }));
      } finally { selecting = false; }
    }

    doc.addEventListener('focusin', event => {
      if (event.target.matches?.(INPUT_SELECTOR)) render(event.target);
    });
    for (const name of ['input', 'change', 'compositionend']) {
      doc.addEventListener(name, event => {
        if (!event.target.matches?.(INPUT_SELECTOR)) return;
        if (event.isComposing) close();
        else render(event.target);
      });
    }
    doc.addEventListener('compositionstart', event => { if (event.target === activeInput) close(); });
    doc.addEventListener('focusout', event => {
      if (event.target === activeInput || list.contains(event.target)) {
        if (event.relatedTarget !== activeInput && !list.contains(event.relatedTarget)) close();
      }
    });
    doc.addEventListener('pointerdown', event => {
      if (list.contains(event.target)) {
        if (event.pointerType === 'mouse') event.preventDefault();
      } else if (event.target !== activeInput) close();
    }, true);
    list.addEventListener('click', event => choose(event.target.closest('[role="option"]')));
    list.addEventListener('pointermove', event => {
      if (event.pointerType !== 'mouse') return;
      const option = event.target.closest('[role="option"]');
      if (option) setActive(Array.from(list.children).indexOf(option));
    });
    doc.addEventListener('keydown', event => {
      const input = event.target;
      if (!input.matches?.(INPUT_SELECTOR) || event.isComposing) return;
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        if (!activeInput) render(input);
        if (activeInput !== input) return;
        event.preventDefault();
        event.stopPropagation();
        setActive(activeIndex < 0 ? (event.key === 'ArrowDown' ? 0 : list.children.length - 1)
          : activeIndex + (event.key === 'ArrowDown' ? 1 : -1));
      } else if (activeInput === input && event.key === 'Enter' && activeIndex >= 0) {
        event.preventDefault();
        event.stopPropagation();
        choose(list.children[activeIndex]);
      } else if (activeInput === input && event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        close();
      } else if (event.key === 'Tab') close();
    }, true);
    doc.addEventListener('submit', () => close(), true);
    doc.addEventListener('reset', () => close(), true);
    doc.addEventListener('close', event => { if (event.target.matches?.('dialog')) close(event.target); }, true);
    doc.addEventListener('shopify:section:load', event => scan(event.target));
    doc.addEventListener('scroll', event => { if (!list.contains(event.target)) schedulePosition(); }, true);
    win.addEventListener('resize', schedulePosition);
    win.visualViewport?.addEventListener('resize', schedulePosition);
    win.visualViewport?.addEventListener('scroll', schedulePosition);
    // Delegation covers new inputs; mutations only reposition/close an already open list.
    new win.MutationObserver(records => {
      if (activeInput && records.some(record => !list.contains(record.target)
        && (record.type === 'childList' || record.target.contains(activeInput)))) schedulePosition();
    }).observe(doc.body, { childList: true, subtree: true, attributes: true,
      attributeFilter: ['hidden', 'inert', 'aria-hidden', 'style', 'class', 'disabled', 'readonly', 'type', 'open'] });
    scan(doc);
    return { bind, scan, close };
  }

  function initEmailAutocomplete(root = globalScope.document) {
    const doc = root?.ownerDocument || root;
    if (!doc?.body || !doc.defaultView) return;
    if (!instances.has(doc)) instances.set(doc, createInstance(doc));
    instances.get(doc).scan(root);
    return instances.get(doc);
  }

  function bindEmailAutocomplete(root, documentRef = globalScope.document) {
    const instance = initEmailAutocomplete(documentRef);
    if (root) instance?.scan(root);
    return instance;
  }

  function close(root, documentRef = globalScope.document) {
    instances.get(documentRef)?.close(root);
  }

  const api = { EMAIL_DOMAINS, MAX_SUGGESTIONS, getEmailSuggestions, bindEmailAutocomplete, initEmailAutocomplete, close };
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (globalScope.document) {
    globalScope.BeetlesEmailAutocomplete = api;
    if (globalScope.document.readyState === 'loading') {
      globalScope.document.addEventListener('DOMContentLoaded', () => initEmailAutocomplete(), { once: true });
    } else initEmailAutocomplete();
  }
})(typeof window !== 'undefined' ? window : globalThis);
